import { ObokuErrorCode, ObokuSharedError } from "@oboku/shared"
import { memo, useCallback, useEffect, useRef, useState } from "react"
import { firstValueFrom } from "rxjs"
import { isPluginType } from "../../integrations/plugins/configure"
import { PluginDownloadBook } from "../../integrations/plugins/PluginDownloadBook"
import type { DownloadBookResult } from "../../integrations/plugins/types"
import { CancelError, ERROR_NO_LINK_MESSAGE } from "../../errors/errors.shared"
import { latestDatabase$ } from "../../rxdb/RxDbProvider"
import { getLinkStateAsync } from "../../links/states"
import { dexieDb } from "../../rxdb/dexie"
import { bytesToMb } from "../../common/utils"
import { DownloadState, booksDownloadStateSignal } from "../states"
import { Logger } from "../../debug/logger.shared"
import type { DownloadFlowRequest } from "./types"

type DownloadLink = NonNullable<Awaited<ReturnType<typeof getLinkStateAsync>>>

const setDownloadData = (
  bookId: string,
  data: ReturnType<typeof booksDownloadStateSignal.getValue>[number],
) => {
  booksDownloadStateSignal.update((prev) => ({
    ...prev,
    [bookId]: {
      ...prev[bookId],
      ...data,
    },
  }))
}

export const DownloadFlowRequestItem = memo(function DownloadFlowRequestItem({
  onSettled,
  request,
}: {
  onSettled: () => void
  request: DownloadFlowRequest
}) {
  // React Compiler cannot lower this function yet, so it bails out here.
  // TODO: re-check on a newer React Compiler; drop this opt-out once it compiles.
  "use no memo"

  const [link, setLink] = useState<DownloadLink | null>(null)
  const [isPreparing, setIsPreparing] = useState(!request.file)
  const { abortController, bookId, file, links, reject, resolve } = request
  const isSettledRef = useRef(false)

  const setProgress = useCallback(
    (progress: number) => {
      setDownloadData(bookId, {
        downloadProgress: Math.round(progress * 100),
      })
    },
    [bookId],
  )

  const settle = useCallback(
    ({ error, success = false }: { error?: unknown; success?: boolean }) => {
      if (isSettledRef.current) {
        return
      }

      isSettledRef.current = true
      onSettled()

      if (success) {
        setDownloadData(bookId, {
          downloadProgress: 100,
          downloadState: DownloadState.Downloaded,
        })
        resolve()

        return
      }

      setDownloadData(bookId, {
        downloadState: DownloadState.None,
      })

      reject(error)
    },
    [bookId, onSettled, reject, resolve],
  )

  const persistDownloadResult = useCallback(
    async (result: DownloadBookResult) => {
      const data = result.data
      const filename = result.fileName.trim()

      if (!filename) {
        throw new Error("Downloaded file is missing a filename.")
      }

      const cachedData =
        data instanceof File
          ? data
          : new File([data], filename, { type: data.type })

      Logger.log(
        `Saving ${bookId} into storage for a size of ${bytesToMb(data.size)} mb`,
      )

      await dexieDb.downloads.add({
        id: bookId,
        data: cachedData,
        filename,
      })
    },
    [bookId],
  )

  const onResolve = useCallback(
    (result: DownloadBookResult) => {
      void persistDownloadResult(result)
        .then(() => {
          settle({ success: true })
        })
        .catch((error) => {
          settle({ error })
        })
    },
    [persistDownloadResult, settle],
  )

  const onError = useCallback(
    (error: unknown) => {
      settle({ error })
    },
    [settle],
  )

  // User cancel: signal abort is handled here; provider also receives signal and may call onError(CancelError); settle guard makes duplicate no-op.
  useEffect(() => {
    const handleAbort = () => {
      onError(new CancelError())
    }

    abortController.signal.addEventListener("abort", handleAbort, {
      once: true,
    })

    return () => {
      abortController.signal.removeEventListener("abort", handleAbort)
    }
  }, [abortController, onError])

  useEffect(
    function prepareRemoteDownload() {
      ;(async () => {
        setDownloadData(bookId, {
          downloadProgress: 0,
          downloadState: DownloadState.Downloading,
        })

        if (file) {
          try {
            await persistDownloadResult({
              data: file,
              fileName: file.name,
            })
            settle({ success: true })
          } catch (error) {
            onError(error)
          }

          return
        }

        try {
          const database = await firstValueFrom(latestDatabase$)
          const resolvedLink = await getLinkStateAsync({
            linkId: links[0] || "",
            db: database,
          })

          if (!resolvedLink) {
            throw new ObokuSharedError(
              ObokuErrorCode.ERROR_NO_LINK,
              new Error(ERROR_NO_LINK_MESSAGE),
            )
          }

          const fileExist = await dexieDb.downloads.get({
            id: bookId,
          })

          if (fileExist) {
            settle({ success: true })

            return
          }

          if (!isPluginType(resolvedLink.type)) {
            throw new Error(`Unsupported link type: ${resolvedLink.type}`)
          }

          setLink(resolvedLink)
          setIsPreparing(false)
        } catch (error) {
          onError(error)
        }
      })()
    },
    [bookId, file, links, onError, persistDownloadResult, settle],
  )

  if (file || isPreparing || !link) {
    return null
  }

  const downloadProps = {
    onDownloadProgress: setProgress,
    onError,
    onResolve,
    signal: abortController.signal,
  }

  return <PluginDownloadBook {...downloadProps} link={link} />
})
